import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { TemplateCategoryPill } from '@/components/marketing/templateCategoryPill';

export interface TemplatePickerTemplate {
  id: string;
  name: string;
  category?: string;
}


export function TemplatePicker({
  templates,
  selectedId,
  onSelect,
  placeholder = 'Select template',
  disabled = false,
  triggerTestId = 'campaign-template-trigger',
  searchTestId = 'campaign-template-search',
}: {
  templates: TemplatePickerTemplate[];
  selectedId: string;
  onSelect: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  triggerTestId?: string;
  searchTestId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.category || '').toLowerCase().includes(q)
    );
  }, [query, templates]);

  const selected = templates.find((t) => t.id === selectedId) ?? null;

  return (
    <Popover open={disabled ? false : open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={triggerTestId}
          disabled={disabled}
          className="w-full flex items-center justify-between rounded-lg bg-white outline-none focus:border-[#0EB64F] transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          style={{ border: '1px solid #EDF1F7', height: 38, padding: '0 11px', fontFamily: 'var(--wn-font)' }}
        >
          <span
            className="truncate"
            style={{ fontSize: 13, fontWeight: 500, color: selected ? '#101828' : '#6A7282' }}
          >
            {selected ? selected.name : placeholder}
          </span>
          <ChevronDown className="w-4 h-4 text-[#98A2B3] flex-shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        className="p-0 rounded-xl border border-[#EDF1F7] shadow-lg bg-white"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <div className="px-3 py-2.5 border-b border-[#EDF1F7]">
          <div className="flex items-center gap-2 h-8 px-2.5 rounded-lg bg-[#FBFCFD] border border-[#EDF1F7]">
            <Search className="w-3.5 h-3.5 text-[#99A1AF] flex-shrink-0" />
            <input
              autoFocus
              data-testid={searchTestId}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search templates…"
              className="flex-1 min-w-0 bg-transparent border-none outline-none text-[13px] text-[#101828] placeholder:text-[#99A1AF]"
              style={{ fontFamily: 'var(--wn-font)' }}
            />
          </div>
        </div>
        <div className="max-h-[260px] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="px-4 py-4 text-center text-[13px]" style={{ fontFamily: 'var(--wn-font)', color: '#6A7282' }}>
              No templates found.
            </p>
          ) : (
            filtered.map((t) => {
              const isCurrent = t.id === selectedId;
              const cat = (t.category || 'MARKETING').toUpperCase();
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { onSelect(t.id); setOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[#F4F6FA]"
                  style={{ background: isCurrent ? '#F4F6FA' : undefined }}
                >
                  <span
                    className="flex-1 min-w-0 truncate leading-none text-[13px] font-medium"
                    style={{ fontFamily: 'var(--wn-font)', color: '#364153' }}
                  >
                    {t.name}
                  </span>
                  <TemplateCategoryPill category={cat} />
                  {isCurrent && <Check className="w-3.5 h-3.5 text-[#0EB64F] flex-shrink-0" strokeWidth={2.5} />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
